import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import clsx from "clsx"

import { ReactComponent as ChevronIcon } from "assets/icons/chevron-icon.svg"

import { SidebarBadge } from "components/badges/SidebarBadge"

import { NavGroupItem, NavLinkItem } from "./navConfig"
import { SidebarLink } from "./SidebarLink"

type Props = {
  group: NavGroupItem
  collapsed: boolean
  darkMode: boolean
  expanded: boolean
  onToggle: () => void
  tLabel: (label: string) => string
  getBadgeValue: (badge?: string) => number | undefined
  navItemClass: (url: string, opts?: { compact?: boolean }) => string
  groupHeaderClass: (isActive: boolean) => string
  isActiveUrl: (url: string) => boolean
  onNavigate: () => void
}

export const SidebarGroup = ({
  group,
  collapsed,
  expanded,
  onToggle,
  tLabel,
  getBadgeValue,
  navItemClass,
  groupHeaderClass,
  isActiveUrl,
  onNavigate
}: Props) => {
  const GroupIcon = group.icon
  const groupLabel = tLabel(group.label)

  const groupIsActive = group.items.some((x) => isActiveUrl(x.url))
  const groupBadgeValue = group.items.reduce((sum, child) => sum + (getBadgeValue(child.badge) ?? 0), 0) || undefined

  const innerRef = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number>(0)

  useLayoutEffect(() => {
    if (collapsed) {
      setHeight(0)
      return
    }

    const el = innerRef.current
    if (!el) return

    if (expanded) {
      setHeight(el.scrollHeight)
    } else {
      setHeight(0)
    }
  }, [expanded, collapsed, group.items.length])

  useEffect(() => {
    if (!expanded || collapsed) return
    const el = innerRef.current
    if (!el) return

    const ro = new ResizeObserver(() => {
      setHeight(el.scrollHeight)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [expanded, collapsed])

  const wrapperStyle: React.CSSProperties = collapsed ? { height: 0 } : { height }

  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => {
          if (!collapsed) onToggle()
        }}
        className={clsx(groupHeaderClass(groupIsActive))}
        title={collapsed ? groupLabel : undefined}
      >
        <div className="flex items-center justify-center">
          <GroupIcon className="w-[22px] h-[22px]" />
        </div>

        {!collapsed && (
          <>
            <span className="flex-1 text-left font-medium">{groupLabel}</span>
            {<SidebarBadge value={groupBadgeValue} expanded={expanded} />}
            <span className={clsx("transition-transform duration-200", expanded ? "rotate-0" : "-rotate-90")}>
              <ChevronIcon className="w-4 h-4 " />
            </span>
          </>
        )}
      </button>

      {!collapsed && (
        <div
          className={clsx("overflow-hidden transition-[height] duration-200 ease-in-out", expanded ? "opacity-100" : "opacity-0")}
          style={wrapperStyle}
          aria-hidden={!expanded}
        >
          <div
            ref={innerRef}
            className={clsx(
              "ml-4 pl-3 border-l border-white/10 space-y-1 mt-1",
              "transition-all duration-200 ease-in-out",
              expanded ? "translate-y-0" : "-translate-y-1"
            )}
          >
            {group.items.map((child: NavLinkItem) => {
              const label = tLabel(child.label)
              const badgeValue = getBadgeValue(child.badge)

              return (
                <SidebarLink
                  key={child.url}
                  item={child}
                  label={label}
                  collapsed={false}
                  badgeValue={badgeValue}
                  onNavigate={onNavigate}
                  className={navItemClass(child.url, { compact: true })}
                  iconClassName="w-[18px] h-[18px]"
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
